import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short human label for the order being rejected (e.g. its SO number). */
  orderLabel: string;
  isPending: boolean;
  onConfirm: (reason: string) => void;
}

/** Small dialog that requires a reason before an order can be rejected. */
export function RejectOrderDialog({ open, onOpenChange, orderLabel, isPending, onConfirm }: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject order {orderLabel}</DialogTitle>
          <DialogDescription>
            Let the person who placed this order know why it&apos;s being rejected.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="rejectReason" className="text-foreground text-sm font-medium">
              Reason
            </label>
            <Input
              id="rejectReason"
              placeholder="e.g. Quantity exceeds the approved budget"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="destructive" type="submit" disabled={isPending || !reason.trim()}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
